import { z } from "zod";

import { LiEventsScrapeStatus } from "@/enum/scrape-enum";
import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { ApolloType } from "@/enum/apollo.enum";
import { LiEventType } from "@/enum/lievent.enum";

import { ListStatus } from "@/enum/list.enum";

import { SuggestionSchema } from "../../LiPeople/schema/liPeople";
import { LiEventEntrySchema } from "./liEventEntry";

const { REQUIRED } = ERROR_TYPE;

export const LiEventSchema = z.object({
	id: z.number(),
	name: z.string().min(2, { message: REQUIRED }).trim(),
	searchTerm: z.string().trim().nullable(),
	type: z.nativeEnum(LiEventType),
	eventUrls: z.array(z.string()),
	status: z.nativeEnum(LiEventsScrapeStatus),
	activeStatus: z.nativeEnum(ListStatus),
	liEventEntries: z.array(LiEventEntrySchema),
	location: z.array(z.string()),
	industry: z.array(z.string()),
	ListLiEvent: z
		.object({
			listItemType: z.nativeEnum(ApolloType),
		})
		.optional()
		.nullable(),
	lastRunAt: z.string().nullable(),
	searchTermInTitle: z.boolean(),
	listId: z.number().optional(),
	locationList: z.array(SuggestionSchema).optional().nullable(),
	industryList: z.array(SuggestionSchema).optional().nullable(),
	titleKeyword: z.string().optional(),
	firstConnection: z.boolean().optional(),
	secondConnection: z.boolean().optional(),
	thirdConnection: z.boolean().optional(),
	now: z.boolean(),
	xDaysBeforeEvent: z.number().nullable(),
	atEventDate: z.boolean(),
	fromOrganization: SuggestionSchema.optional().nullable(),
	alsoPastEvents: z.boolean(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const SubLiEventSchema = z.object({
	id: z.number(),
	name: z.string().min(2, { message: REQUIRED }).trim(),
	searchTerm: z.string().trim().nullable(),
	type: z.nativeEnum(LiEventType),
	eventUrls: z.array(z.string()),
	status: z.nativeEnum(LiEventsScrapeStatus),
	activeStatus: z.nativeEnum(ListStatus),
	liEventEntries: z.array(LiEventEntrySchema),
	location: z.array(z.string()),
	industry: z.array(z.string()),
	minDaysBeforeScrape: z.number(),
	minAttendeesBeforeScrape: z.number().nullable(),
	ListLiEvent: z
		.object({
			listItemType: z.nativeEnum(ApolloType),
		})
		.optional()
		.nullable(),
	lastRunAt: z.string().nullable(),
	titleKeyword: z.string().optional(),
	firstConnection: z.boolean().optional(),
	secondConnection: z.boolean().optional(),
	thirdConnection: z.boolean().optional(),
	now: z.boolean(),
	xDaysBeforeEvent: z.number().nullable(),
	atEventDate: z.boolean(),
	fromOrganization: SuggestionSchema.optional().nullable(),
	alsoPastEvents: z.boolean(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const CreateLiEventSchema = z
	.object({
		id: z.number().optional(),
		name: z.string().min(2, { message: REQUIRED }).trim(),
		searchTerm: z.string().trim().optional(),
		type: z.nativeEnum(LiEventType),
		eventUrls: z.array(z.string()).optional(),
		listId: z.number().optional(),
		searchTermInTitle: z.boolean(),
		locationList: z.array(SuggestionSchema).optional().nullable(),
		industryList: z.array(SuggestionSchema).optional().nullable(),
		titleKeyword: z.string().optional(),
		firstConnection: z.boolean().optional(),
		secondConnection: z.boolean().optional(),
		thirdConnection: z.boolean().optional(),
		now: z.boolean(),
		xDaysBeforeEvent: z.number().nullable(),
		atEventDate: z.boolean(),
		fromOrganization: SuggestionSchema.optional().nullable(),
		alsoPastEvents: z.boolean(),
		plannedScrapingUserId: z.number().optional().nullable(),
	})
	.refine(
		(data) => {
			if (!data.xDaysBeforeEvent && !data.atEventDate && !data.now) {
				return false;
			}

			return true;
		},
		{
			message: ERROR_TYPE.REQUIRED,
			path: ["now"],
		}
	)
	.refine(
		(data) => {
			if (
				data.eventUrls &&
				data.eventUrls.length > 0 &&
				!data.eventUrls?.[0]?.includes("linkedin.com/events/")
			) {
				return false;
			}

			return true;
		},
		{
			message: ERROR_TYPE.INVALID_LINKEDIN_EVENT_URL,
			path: ["eventUrls"],
		}
	);

export const LiEventResponseSchema = z.object({
	data: z.array(LiEventSchema),
	meta: MetaDataSchema,
});

export const ValidateEventUrlResponseSchema = z.object({
	url: z.string(),
	title: z.string().nullable(),
	description: z.string().nullable(),
	eventAt: z.string().nullable(),
	companyName: z.string().nullable(),
	urn: z.string().nullable(),
});

export type LiEvent = z.infer<typeof LiEventSchema>;

export type CreateLiEvent = z.infer<typeof CreateLiEventSchema>;

export type LiEventResponse = z.infer<typeof LiEventResponseSchema>;

export type ValidateEventUrlResponse = z.infer<typeof ValidateEventUrlResponseSchema>;
